import type { CreditLimitStatus } from '@monolith/legacy/services/api/ankorpay';
import {
  AnkorstoreInternalPspRetailerAccountResourceStateEnum as BackendPspState,
  PaymentListPspsStatusEnum as BusinessVerificationStatus,
} from '@ankorstore/api-internal/data-contracts';

// Re-export for consumers
export { ARIA_PSP_NAME, AKS_PSP_NAME } from '@monolith/legacy/services/api/ankorpay';
export { AnkorstoreInternalPspRetailerAccountResourceStateEnum as BackendPspState } from '@ankorstore/api-internal/data-contracts';
// Both feeds (RCN-499's meta.businessVerification and RCN-498's CMS header-pills
// business_verification) carry the same values under different codegen-generated enums.
// PaymentListPspsStatusEnum is the canonical export; cast GetCmsHeaderPillsStatusEnum1
// to this type at call sites in the CMS feed path.
export { PaymentListPspsStatusEnum as BusinessVerificationStatus } from '@ankorstore/api-internal/data-contracts';

export enum CreditLimitState {
  LOADING = 'loading',
  HIDDEN = 'hidden',
  APPLY = 'apply',
  PROCESSING = 'processing',
  REFUSED = 'refused',
  FROZEN = 'frozen',
  ACTIVE = 'active',
  ACTIVE_NO_MANDATE = 'active_no_mandate',
  ACTIVE_WITH_MANDATE = 'active_with_mandate',
  // No separate "expired" state — the backend doesn't distinguish an expired KYC link from one
  // that was never started, so both resolve here.
  KYC_REQUIRED = 'kyc_required',
  KYC_PENDING = 'kyc_pending',
  // Ankorstore's own payment-terms verification gate — distinct from the Aria KYC
  // states above, which come from credit_limit.refusalReasons at a later point in the journey.
  BUSINESS_VERIFICATION_REQUIRED = 'business_verification_required',
  BUSINESS_VERIFICATION_IN_REVIEW = 'business_verification_in_review',
  BUSINESS_VERIFICATION_ESCALATED = 'business_verification_escalated',
  BUSINESS_VERIFICATION_REJECTED = 'business_verification_rejected',
}

export const ACTIVE_STATUSES: CreditLimitStatus[] = ['ACCEPTED', 'REVISED'];
export const PROCESSING_STATUSES: CreditLimitStatus[] = ['PROCESSING', 'ONGOING_REVISION'];

export enum CreditLimitCmsStatus {
  INCOMPLETE = 'incomplete',
  FROZEN = 'frozen',
  REFUSED = 'refused',
}

// One of the values in the CMS header-pills `refusalReasons` array: company legal information
// is not publicly available in the registry, so the retailer must publish it in full.
export const LEGAL_INFORMATION_PROTECTED_REASON = 'LEGAL_INFORMATION_PROTECTED';

// The CMS has no dedicated top-level status for KYC — it rides on the existing REFUSED/INCOMPLETE
// statuses, disambiguated by these `refusalReasons` entries.
export const KYC_REQUIRED_REASON = 'KYC_PENDING';
export const KYC_IN_REVIEW_REASON = 'KYC_IN_REVIEW';

/** Map backend PspState enum to FE CreditLimitState. Exhaustive — TS compile error if backend adds a 9th value. */
export function mapBackendStateToFrontend(backendState: BackendPspState): CreditLimitState {
  switch (backendState) {
    case BackendPspState.ACTIVE_WITH_MANDATE:
      return CreditLimitState.ACTIVE_WITH_MANDATE;
    case BackendPspState.ACTIVE_NO_MANDATE:
      return CreditLimitState.ACTIVE_NO_MANDATE;
    case BackendPspState.APPLY:
      return CreditLimitState.APPLY;
    case BackendPspState.KYC_PENDING:
      // Covers provider status `pending` and `expired` alike — same actionable "KYC required" card/CTA.
      return CreditLimitState.KYC_REQUIRED;
    case BackendPspState.KYC_IN_REVIEW:
      // KYC submitted, awaiting Aria review — non-actionable "under review" card, no CTA.
      return CreditLimitState.KYC_PENDING;
    case BackendPspState.PROCESSING:
      return CreditLimitState.PROCESSING;
    case BackendPspState.FROZEN:
      return CreditLimitState.FROZEN;
    case BackendPspState.REFUSED_BI_UPDATE:
    case BackendPspState.REFUSED_GENERIC:
    // Non-public legal information blocks credit assessment, so it counts as a refusal.
    case BackendPspState.LEGAL_INFORMATION_PROTECTED:
      return CreditLimitState.REFUSED;
    default:
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      return ((_exhaustive: never): CreditLimitState => CreditLimitState.HIDDEN)(backendState);
  }
}

/**
 * Map a business_verification status to a UI state. Null means "no verification pill" — either
 * `approved` (fall through to the normal credit pill/cards) or absent (out of scope entirely).
 * Exhaustive — TS compile error if backend adds a 7th value.
 */
export function mapBusinessVerificationStatusToState(status: BusinessVerificationStatus): CreditLimitState | null {
  switch (status) {
    case BusinessVerificationStatus.Required:
    case BusinessVerificationStatus.Expired:
      // Expired renders identically to required — the retailer just restarts the same journey.
      return CreditLimitState.BUSINESS_VERIFICATION_REQUIRED;
    case BusinessVerificationStatus.InReview:
      return CreditLimitState.BUSINESS_VERIFICATION_IN_REVIEW;
    case BusinessVerificationStatus.Escalated:
      return CreditLimitState.BUSINESS_VERIFICATION_ESCALATED;
    case BusinessVerificationStatus.Rejected:
      return CreditLimitState.BUSINESS_VERIFICATION_REJECTED;
    case BusinessVerificationStatus.Approved:
      return null;
    default:
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      return ((_exhaustive: never): null => null)(status);
  }
}

/** True when the retailer can submit a BI update to attempt credit re-activation. */
export function isRefusedBiUpdate(backendState: BackendPspState): boolean {
  return backendState === BackendPspState.REFUSED_BI_UPDATE;
}

/**
 * True when the credit limit is refused because the retailer's legal information is not
 * publicly available in the registry. Actionable: the retailer can switch their registry
 * visibility to full publication to regain payment-terms eligibility.
 */
export function isLegalInformationProtected(backendState: BackendPspState): boolean {
  return backendState === BackendPspState.LEGAL_INFORMATION_PROTECTED;
}

/** False for HIDDEN / null / undefined; true for all other states. Used by visibility guards. */
export function isVisibleState(state: CreditLimitState | null | undefined): boolean {
  return state !== undefined && state !== null && state !== CreditLimitState.HIDDEN;
}

/** Active states that expose a usable credit balance (FROZEN excluded — it shows a dedicated card regardless of amount). */
export function isActiveNonFrozenState(state: CreditLimitState | null | undefined): boolean {
  return (
    state === CreditLimitState.ACTIVE ||
    state === CreditLimitState.ACTIVE_NO_MANDATE ||
    state === CreditLimitState.ACTIVE_WITH_MANDATE
  );
}

/** Map Aria CreditLimitStatus to a UI state (simple, without mandate/frozen details) */
export function getStateFromStatus(statusValue: CreditLimitStatus): CreditLimitState {
  if (!statusValue) {
    return CreditLimitState.HIDDEN;
  }
  if (ACTIVE_STATUSES.includes(statusValue)) {
    return CreditLimitState.ACTIVE;
  }
  if (PROCESSING_STATUSES.includes(statusValue)) {
    return CreditLimitState.PROCESSING;
  }
  if (statusValue === 'REFUSED') {
    return CreditLimitState.REFUSED;
  }
  return CreditLimitState.HIDDEN;
}
